import { useState } from 'react'
import type { FormEvent } from 'react'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { api, ApiError } from '../../lib/api'
import { formatDate, formatNumber, titleCase } from '../../lib/format'
import type { HiredEquipment, Organization } from '../../types'
import {
  Alert, Button, Card, CardBody, CardHeader, EmptyState, Input, Select, Skeleton,
} from '../../components/ui'
import { Wizard } from '../../components/Wizard'
import { CloseIcon, PlusIcon, WrenchIcon } from '../../components/icons'

/* Platform-wide hired/owned equipment ledger - port of
 * admin/hired_equipment.html + admin/register_hired_equipment.html
 * (admin/routes.py hired_equipment_list()/hired_equipment_register()).
 * Distinct from the much larger equipment_service.py maintenance
 * knowledge-base system, which stays Jinja-only for now. */

interface EquipmentResponse { ok: boolean; items: HiredEquipment[]; types: string[]; statuses: string[] }

export default function AdminHiredEquipment() {
  const qc = useQueryClient()
  const [showForm, setShowForm] = useState(false)
  const [status, setStatus] = useState('')

  const { data, isLoading, isError, error } = useQuery({
    queryKey: ['admin', 'hired-equipment', status],
    queryFn: () => api.get<EquipmentResponse>(`/admin/hired-equipment${status ? `?status=${status}` : ''}`),
  })
  const orgsQuery = useQuery({
    queryKey: ['admin', 'orgs', 'picker'],
    queryFn: () => api.get<{ organizations: Organization[] }>('/admin/orgs?status=&limit=1000'),
    enabled: showForm,
  })

  const create = useMutation({
    mutationFn: (body: Record<string, unknown>) => api.post('/admin/hired-equipment', body),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['admin', 'hired-equipment'] })
      setShowForm(false)
    },
  })

  const setEquipStatus = useMutation({
    mutationFn: ({ id, status: newStatus }: { id: string; status: string }) =>
      api.patch(`/admin/hired-equipment/${id}`, { status: newStatus }),
    onSuccess: () => void qc.invalidateQueries({ queryKey: ['admin', 'hired-equipment'] }),
  })

  function onSubmit(e: FormEvent<HTMLFormElement>) {
    e.preventDefault()
    const form = new FormData(e.currentTarget)
    create.mutate({
      org_id: String(form.get('org_id') ?? ''),
      name: String(form.get('name') ?? '').trim(),
      equipment_type: String(form.get('equipment_type') ?? 'other'),
      ownership: String(form.get('ownership') ?? 'hired'),
      supplier_name: String(form.get('supplier_name') ?? '').trim(),
      supplier_contact: String(form.get('supplier_contact') ?? '').trim(),
      hire_start: String(form.get('hire_start') ?? '') || undefined,
      hire_end: String(form.get('hire_end') ?? '') || undefined,
      daily_rate: form.get('daily_rate') ? Number(form.get('daily_rate')) : undefined,
      currency: String(form.get('currency') ?? 'ZMW').trim() || 'ZMW',
      notes: String(form.get('notes') ?? '').trim(),
    })
  }

  const items = data?.items ?? []
  const types = data?.types ?? []
  const statuses = data?.statuses ?? []

  return (
    <>
      <div className="page-head">
        <div>
          <h1>Hired &amp; owned equipment</h1>
          <p className="page-head__sub">Machinery in use across every organisation — excavators, generators, drill rigs and more.</p>
        </div>
        <Button className="fab-trigger__inline" icon={<PlusIcon />} onClick={() => setShowForm((s) => !s)}>
          {showForm ? 'Cancel' : 'Register equipment'}
        </Button>
        <button
          type="button" className="fab"
          onClick={() => setShowForm((s) => !s)}
          aria-label={showForm ? 'Cancel' : 'Register equipment'}
        >
          {showForm ? <CloseIcon size={22} /> : <PlusIcon size={22} />}
        </button>
      </div>

      {showForm && (
        <Card style={{ marginBottom: 'var(--sp-6)' }}>
          <CardHeader title="Register equipment" />
          <CardBody>
            <form onSubmit={onSubmit}>
              <div className="stack" style={{ gap: 'var(--sp-4)' }}>
                {create.isError && (
                  <Alert tone="danger">
                    {create.error instanceof ApiError ? create.error.message : 'Could not register that equipment.'}
                  </Alert>
                )}
                {/* PWA: 3 guided steps; desktop stacked, unchanged. */}
                <Wizard
                  steps={[
                    { title: 'What & whose', content: (
                      <div className="stack">
                        <Select label="Organisation" name="org_id" required defaultValue="">
                          <option value="" disabled>Select an organisation…</option>
                          {(orgsQuery.data?.organizations ?? []).map((o) => (
                            <option key={o.id} value={o.id}>{o.name}</option>
                          ))}
                        </Select>
                        <div className="grid grid--2">
                          <Input label="Name" name="name" required />
                          <Select label="Type" name="equipment_type" defaultValue="other">
                            {(types.length ? types : ['other']).map((t) => <option key={t} value={t}>{titleCase(t)}</option>)}
                          </Select>
                        </div>
                      </div>
                    ) },
                    { title: 'Ownership & supplier', content: (
                      <div className="stack">
                        <Select label="Ownership" name="ownership" defaultValue="hired">
                          <option value="owned">Owned</option>
                          <option value="hired">Hired</option>
                        </Select>
                        <div className="grid grid--2">
                          <Input label="Supplier (if hired)" name="supplier_name" />
                          <Input label="Supplier contact" name="supplier_contact" />
                        </div>
                      </div>
                    ) },
                    { title: 'Hire period', content: (
                      <div className="grid grid--2">
                        <Input label="Hire start" name="hire_start" type="date" />
                        <Input label="Hire end" name="hire_end" type="date" />
                      </div>
                    ) },
                    { title: 'Cost & notes', content: (
                      <div className="stack">
                        <div className="grid grid--2">
                          <Input label="Daily rate" name="daily_rate" type="number" step="any" min="0" />
                          <Input label="Currency" name="currency" defaultValue="ZMW" />
                        </div>
                        <Input label="Notes (optional)" name="notes" />
                      </div>
                    ) },
                  ]}
                  footer={(
                    <div className="row">
                      <Button type="submit" loading={create.isPending}>Register</Button>
                    </div>
                  )}
                />
              </div>
            </form>
          </CardBody>
        </Card>
      )}

      <div className="row" style={{ flexWrap: 'wrap', gap: 'var(--sp-3)', marginBottom: 'var(--sp-5)', alignItems: 'flex-end' }}>
        <Select label="Status" value={status} onChange={(e) => setStatus(e.target.value)} style={{ maxWidth: 200 }}>
          <option value="">All statuses</option>
          {statuses.map((s) => <option key={s} value={s}>{titleCase(s)}</option>)}
        </Select>
      </div>

      <Card>
        <CardHeader title="Equipment" sub={items.length ? `${items.length} on file` : undefined} />
        <CardBody flush>
          {isError ? (
            <div style={{ padding: 'var(--sp-6)' }}><Alert tone="danger">{(error as Error).message}</Alert></div>
          ) : isLoading ? (
            <div style={{ padding: 'var(--sp-6)' }} className="stack"><Skeleton h={20} /><Skeleton h={20} /></div>
          ) : items.length === 0 ? (
            <EmptyState icon={<WrenchIcon size={22} />} title="No equipment found" body="Registered equipment will appear here." />
          ) : (
            <div className="table-wrap">
              <table className="table">
                <thead><tr><th>Name</th><th>Type</th><th>Ownership</th><th>Supplier</th><th>Hired since</th><th className="table__num">Daily rate</th><th>Status</th></tr></thead>
                <tbody>
                  {items.map((eq) => (
                    <tr key={eq.id}>
                      <td style={{ fontWeight: 600 }}>{eq.name || '—'}</td>
                      <td>{titleCase(eq.equipment_type)}</td>
                      <td>{titleCase(eq.ownership)}</td>
                      <td>{eq.supplier_name || '—'}</td>
                      <td>{formatDate(eq.hire_start) || '—'}</td>
                      <td className="table__num">{eq.daily_rate != null ? `${eq.currency || 'ZMW'} ${formatNumber(eq.daily_rate, 2)}` : '—'}</td>
                      <td>
                        <Select
                          value={eq.status || 'active'}
                          disabled={setEquipStatus.isPending && setEquipStatus.variables?.id === eq.id}
                          onChange={(e) => eq.id && setEquipStatus.mutate({ id: eq.id, status: e.target.value })}
                          style={{ minWidth: 130 }}
                        >
                          {(statuses.length ? statuses : ['active']).map((s) => <option key={s} value={s}>{titleCase(s)}</option>)}
                        </Select>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </CardBody>
      </Card>
    </>
  )
}
